import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { Language, Candidate, SourceId, CriterionStatus, SearchCriteria } from '@sourcr/shared';
import { gradeOf, isDemoSource, isAssistSource, SOURCE_LABELS, SOURCES, searchFilters } from '@sourcr/shared';
import { I18N } from '../../lib/i18n';
import { listCandidatesPaged, addFavorite, removeFavorite, listSearches, getLastSearchId, getSearch, submitSignal } from '../../lib/api';
import { GradeChip, ScoreBar } from '../../components/ui/grade-chip';
import { SourceBadge } from '../../components/source-badge';
import { IconStar, IconExternalLink, IconCopy } from '../../components/icons';
import { Skeleton } from '../../components/ui/skeleton';

interface ResultsPageProps {
  lang: Language;
}

const S = {
  page: {
    display: 'flex',
    gap: 20,
    height: 'calc(100dvh - 120px)',
    overflow: 'hidden',
  },
  listPanel: {
    width: 380,
    display: 'flex',
    flexDirection: 'column' as const,
    backgroundColor: 'var(--bg-surface)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    overflow: 'hidden',
    flexShrink: 0,
  },
  listPanelMobile: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    backgroundColor: 'var(--bg-surface)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    overflow: 'hidden',
  },
  listHeader: {
    padding: '14px 16px',
    borderBottom: '1px solid var(--border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  listTitle: { fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', margin: 0 },
  listCount: { fontSize: 13, color: 'var(--text-muted)' },
  filterBar: {
    padding: '8px 16px',
    borderBottom: '1px solid var(--border)',
    display: 'flex',
    gap: 8,
    backgroundColor: 'var(--bg-elevated)',
    flexShrink: 0,
    flexWrap: 'wrap' as const,
  },
  filterSelect: {
    outline: 'none',
    border: '1px solid var(--border)',
    borderRadius: 6,
    padding: '4px 8px',
    backgroundColor: 'var(--bg-base)',
    color: 'var(--text-primary)',
    fontSize: 12,
    cursor: 'pointer',
  },
  filterInput: {
    outline: 'none',
    border: '1px solid var(--border)',
    borderRadius: 6,
    padding: '4px 8px',
    backgroundColor: 'var(--bg-base)',
    color: 'var(--text-primary)',
    fontSize: 12,
    flex: 1,
    minWidth: 80,
  },
  list: { overflowY: 'auto' as const, flex: 1 },
  candidateRow: {
    padding: '12px 16px',
    display: 'flex',
    gap: 12,
    alignItems: 'flex-start',
    cursor: 'pointer',
    borderBottom: '1px solid var(--border)',
    transition: 'background 0.1s',
  },
  candidateInfo: { flex: 1, minWidth: 0 },
  candidateName: {
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--text-primary)',
    marginBottom: 2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  demoBadge: {
    marginLeft: 6,
    fontFamily: 'var(--font-mono)',
    fontSize: 9,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
    color: 'var(--warning)',
    border: '1px solid color-mix(in oklch, var(--warning) 40%, var(--border))',
    borderRadius: 4,
    padding: '1px 5px',
    verticalAlign: 'middle' as const,
  },
  assistBadge: {
    marginLeft: 6,
    fontFamily: 'var(--font-mono)',
    fontSize: 9,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
    color: 'var(--accent)',
    border: '1px solid color-mix(in oklch, var(--accent) 40%, var(--border))',
    borderRadius: 4,
    padding: '1px 5px',
    verticalAlign: 'middle' as const,
  },
  assistNote: {
    fontSize: 12,
    color: 'var(--text-secondary)',
    backgroundColor: 'var(--bg-elevated)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    padding: '8px 12px',
    lineHeight: 1.4,
  },
  candidateWhy: {
    fontSize: 12,
    color: 'var(--text-secondary)',
    lineHeight: 1.4,
    marginBottom: 6,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical' as const,
    overflow: 'hidden',
  },
  candidateTitle: {
    fontSize: 12,
    color: 'var(--text-muted)',
    marginBottom: 6,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  candidateMeta: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' as const },
  skillChip: {
    padding: '2px 7px',
    backgroundColor: 'var(--bg-overlay)',
    borderRadius: 4,
    fontSize: 11,
    color: 'var(--text-secondary)',
    fontFamily: 'var(--font-mono)',
  },
  detailPanel: {
    flex: 1,
    backgroundColor: 'var(--bg-surface)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column' as const,
  },
  detailHeader: {
    padding: '16px 20px',
    borderBottom: '1px solid var(--border)',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 14,
  },
  detailAvatar: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    backgroundColor: 'var(--accent-subtle)',
    color: 'var(--accent)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: 16,
    flexShrink: 0,
  },
  detailName: { fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 2px' },
  detailTitle: { fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 },
  detailActions: { display: 'flex', gap: 8, marginLeft: 'auto', flexShrink: 0 },
  iconBtn: {
    width: 34,
    height: 34,
    borderRadius: 8,
    border: '1px solid var(--border)',
    backgroundColor: 'var(--bg-elevated)',
    color: 'var(--text-secondary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  favBtnActive: {
    width: 34,
    height: 34,
    borderRadius: 8,
    border: 'none',
    backgroundColor: 'var(--accent)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  detailBody: {
    flex: 1,
    overflowY: 'auto' as const,
    padding: '16px 20px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 20,
  },
  section: { display: 'flex', flexDirection: 'column' as const, gap: 8 },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--text-muted)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },
  scoreRow: { display: 'flex', alignItems: 'center', gap: 12 },
  scoreNum: {
    fontSize: 28,
    fontWeight: 700,
    color: 'var(--text-primary)',
    fontFamily: 'var(--font-mono)',
    lineHeight: 1,
  },
  summary: { fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 },
  skillsWrap: { display: 'flex', flexWrap: 'wrap' as const, gap: 6 },
  gapChip: {
    padding: '3px 9px',
    backgroundColor: 'oklch(22% 0.06 22)',
    border: '1px solid oklch(35% 0.1 22)',
    borderRadius: 6,
    fontSize: 12,
    color: 'oklch(70% 0.18 22)',
    fontFamily: 'var(--font-mono)',
  },
  // Per-criterion assessment (P1-3)
  criteriaList: { display: 'flex', flexDirection: 'column' as const, gap: 4 },
  criteriaItem: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, lineHeight: 1.4 },
  criteriaIcon: {
    flexShrink: 0,
    width: 16,
    textAlign: 'center' as const,
    fontFamily: 'var(--font-mono)',
    fontWeight: 700,
  },
  criteriaCriterion: { color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const },
  legend: {
    fontSize: 11,
    color: 'var(--text-muted)',
    marginTop: 6,
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 8,
  },
  // Compact icon strip under each list-row summary
  criteriaStrip: { display: 'flex', gap: 3, marginTop: 4, flexWrap: 'wrap' as const },
  criteriaStripIcon: {
    fontFamily: 'var(--font-mono)',
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1,
  },
  // "Score unavailable" placeholder (honest failure, P0-4)
  naChip: {
    width: 36,
    height: 36,
    borderRadius: 8,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--bg-elevated)',
    border: '1px solid var(--border)',
    color: 'var(--text-muted)',
    fontFamily: 'var(--font-mono)',
    fontSize: 10,
    flexShrink: 0,
  },
  scoreUnavailable: { fontSize: 14, color: 'var(--text-muted)' },
  // Search filters block (include/exclude — P1-1)
  filterItem: { display: 'flex', flexDirection: 'column' as const, gap: 2 },
  filterLabel: { fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' },
  filterValue: { fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.4 },
  emptyDetail: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--text-muted)',
    fontSize: 14,
  },
  loadingRow: {
    padding: '20px 16px',
    color: 'var(--text-muted)',
    fontSize: 13,
    textAlign: 'center' as const,
  },
  backBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 12,
    color: 'var(--text-muted)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    marginBottom: 2,
  },
  exportBtn: {
    fontSize: 11,
    padding: '4px 8px',
    borderRadius: 6,
    border: '1px solid var(--border)',
    backgroundColor: 'var(--bg-elevated)',
    color: 'var(--text-muted)',
    cursor: 'pointer',
    fontFamily: 'var(--font-mono)',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
};

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

/** Icon + colour per criterion verdict (P1-3). */
const STATUS_META: Record<CriterionStatus, { icon: string; color: string }> = {
  match: { icon: '✓', color: 'var(--success)' },
  mismatch: { icon: '✗', color: 'var(--danger)' },
  unknown: { icon: '—', color: 'var(--text-muted)' },
};

function exportCSV(rows: Candidate[]) {
  const header = ['Name', 'Title', 'Location', 'Score', 'Grade', 'Skills', 'Source', 'Profile URL'];
  const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
  const lines = [
    header.join(','),
    ...rows.map((c) => [
      escape(c.name),
      escape(c.title ?? ''),
      escape(c.location ?? ''),
      c.score,
      gradeOf(c.score),
      escape(c.skills.join('; ')),
      c.sourceId,
      escape(c.profileUrl),
    ].join(',')),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `sourcr-candidates-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

type GradeFilter = 'all' | 'A' | 'B' | 'C';

const GRADE_MIN: Record<GradeFilter, number> = {
  all: 0,
  A: 80,
  B: 60,
  C: 40,
};

const MOBILE_BREAKPOINT = 768;

export function ResultsPage({ lang }: ResultsPageProps) {
  const i = I18N[lang].results;
  const { searchId } = useParams<{ searchId?: string }>();
  const navigate = useNavigate();

  // Per-criterion assessment helpers (P1-3)
  const statusLabel = (s: CriterionStatus): string =>
    s === 'match' ? i.critStatusMatch : s === 'mismatch' ? i.critStatusMismatch : i.critStatusUnknown;
  const renderLegend = () => (
    <div style={S.legend}>
      {(['match', 'mismatch', 'unknown'] as CriterionStatus[]).map((s) => (
        <span key={s}>
          <span style={{ color: STATUS_META[s].color, fontWeight: 700 }}>{STATUS_META[s].icon}</span> {statusLabel(s)}
        </span>
      ))}
    </div>
  );

  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Candidate | null>(null);
  const PAGE_SIZE = 20;

  // Mobile responsive state
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < MOBILE_BREAKPOINT);

  // candidateId → favoriteId (populated when a favorite is created)
  const [favoriteMap, setFavoriteMap] = useState<Map<string, string>>(new Map());
  const [favLoading, setFavLoading] = useState<string | null>(null);

  // Filter state
  const [gradeFilter, setGradeFilter] = useState<GradeFilter>('all');
  const [sourceFilter, setSourceFilter] = useState<SourceId | 'all'>('all');
  const [skillQuery, setSkillQuery] = useState('');

  // Per-source honest statuses for the diagnostics strip (P2-1)
  const [sourceStatuses, setSourceStatuses] = useState<Record<string, string>>({});
  // Search criteria — needed for the include/exclude "Search filters" block (P1-1)
  const [searchCriteria, setSearchCriteria] = useState<SearchCriteria | null>(null);

  // Copy state for detail panel
  const [copied, setCopied] = useState(false);

  // Ref for keyboard-selected row scroll
  const selectedRef = useRef<HTMLDivElement | null>(null);

  // Resize listener for mobile detection
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Load the search record's per-source statuses (so users see WHY a search is empty)
  useEffect(() => {
    if (!searchId) return;
    let mounted = true;
    void getSearch(searchId)
      .then((s) => {
        if (!mounted) return;
        setSourceStatuses(s.sourceStatuses ?? {});
        setSearchCriteria((s.criteria as SearchCriteria) ?? null);
      })
      .catch(() => { /* non-fatal */ });
    return () => { mounted = false; };
  }, [searchId]);

  useEffect(() => {
    let mounted = true;
    void (async () => {
      // Bare /results (e.g. after F5) → restore the most recent search instead
      // of a dead empty state.
      if (!searchId) {
        const fallbackId = getLastSearchId();
        if (fallbackId) {
          navigate(`/results/${fallbackId}`, { replace: true });
          return; // effect re-runs with the new :searchId param
        }
        try {
          const { searches: recent } = await listSearches();
          const latest = recent.find((s) => s.status === 'completed') ?? recent[0];
          if (latest && mounted) {
            navigate(`/results/${latest.id}`, { replace: true });
            return;
          }
        } catch { /* fall through to empty state */ }
        if (mounted) setLoading(false);
        return;
      }

      try {
        const { candidates: rows, total: t } = await listCandidatesPaged(searchId, PAGE_SIZE, 0);
        if (!mounted) return;
        setCandidates(rows);
        setTotal(t);
        // On desktop auto-select first; on mobile leave unselected so list shows
        if (!isMobile) setSelected(rows[0] ?? null);
      } catch (e) {
        if (!mounted) return;
        setError(e instanceof Error ? e.message : 'Failed to load candidates');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
    // isMobile intentionally excluded — only re-run when searchId changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchId, navigate]);

  const loadMore = useCallback(async () => {
    if (!searchId || loadingMore) return;
    setLoadingMore(true);
    try {
      const { candidates: rows, total: t } = await listCandidatesPaged(searchId, PAGE_SIZE, candidates.length);
      setCandidates((prev) => [...prev, ...rows]);
      setTotal(t);
    } catch {
      // silently ignore — user can retry by clicking Load More again
    } finally {
      setLoadingMore(false);
    }
  }, [searchId, candidates.length, loadingMore]);

  // Derived: unique source ids from results
  const availableSources = useMemo<SourceId[]>(() => {
    const seen = new Set<SourceId>();
    for (const c of candidates) {
      seen.add(c.sourceId);
    }
    return Array.from(seen);
  }, [candidates]);

  // Composed filtered list
  const filtered = useMemo<Candidate[]>(() => {
    const minScore = GRADE_MIN[gradeFilter];
    const q = skillQuery.toLowerCase().trim();
    return candidates.filter((c) => {
      if (c.score < minScore) return false;
      if (sourceFilter !== 'all' && c.sourceId !== sourceFilter) return false;
      if (q !== '' && !c.skills.some((s) => s.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [candidates, gradeFilter, sourceFilter, skillQuery]);

  const toggleFavorite = useCallback(async (candidate: Candidate) => {
    const existingFavId = favoriteMap.get(candidate.id);
    setFavLoading(candidate.id);
    try {
      if (existingFavId) {
        await removeFavorite(existingFavId);
        setFavoriteMap((m) => {
          const next = new Map(m);
          next.delete(candidate.id);
          return next;
        });
      } else {
        const fav = await addFavorite(candidate.id);
        setFavoriteMap((m) => new Map(m).set(candidate.id, fav.id));
        // Phase 7: saving a candidate is a strong "imported" preference signal.
        submitSignal({ source: candidate.sourceId, url: candidate.profileUrl, signal: 'imported' });
      }
    } catch {
      // silently ignore — user can retry
    } finally {
      setFavLoading(null);
    }
  }, [favoriteMap]);

  const isFavorited = (id: string) => favoriteMap.has(id);

  // Keyboard navigation: ↑/↓ move through filtered list
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (!filtered.length) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const idx = selected ? filtered.findIndex((c) => c.id === selected.id) : -1;
        const next = filtered[Math.min(idx + 1, filtered.length - 1)];
        if (next) setSelected(next);
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        const idx = selected ? filtered.findIndex((c) => c.id === selected.id) : 0;
        const prev = filtered[Math.max(idx - 1, 0)];
        if (prev) setSelected(prev);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [filtered, selected]);

  // Scroll selected item into view when it changes via keyboard
  useEffect(() => {
    selectedRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selected]);

  if (!searchId) {
    return (
      <div style={S.emptyDetail}>
        <span>No search selected. <button style={S.backBtn} onClick={() => navigate('/dashboard')}>Go to dashboard</button></span>
      </div>
    );
  }

  // Mobile: determine which panel to show
  // - no selection → show list
  // - has selection → show detail
  const showListOnMobile = isMobile && selected === null;
  const showDetailOnMobile = isMobile && selected !== null;

  // List panel — shown on desktop always, on mobile only when no selection
  const listPanel = (
    <div style={isMobile ? S.listPanelMobile : S.listPanel}>
      <div style={S.listHeader}>
        <h2 style={S.listTitle}>{i.title}</h2>
        <div style={S.headerRight}>
          <span style={S.listCount}>
            {loading
              ? '…'
              : `${filtered.length} / ${total || candidates.length} ${i.found}`}
          </span>
          {!isMobile && (
            <button
              type="button"
              style={S.exportBtn}
              onClick={() => exportCSV(filtered)}
              disabled={filtered.length === 0}
            >
              {i.exportCSV}
            </button>
          )}
        </div>
      </div>

      {/* Filter bar */}
      <div style={S.filterBar}>
        <select
          value={gradeFilter}
          onChange={(e) => setGradeFilter(e.target.value as GradeFilter)}
          style={S.filterSelect}
          aria-label={i.filterByGrade}
        >
          <option value="all">{i.allGrades}</option>
          <option value="A">{i.gradeA}</option>
          <option value="B">{i.gradeB}</option>
          <option value="C">{i.gradeC}</option>
        </select>

        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value as SourceId | 'all')}
          style={S.filterSelect}
          aria-label={i.filterBySource}
        >
          <option value="all">{i.allSources}</option>
          {availableSources.map((src) => (
            <option key={src} value={src}>{SOURCE_LABELS[src]}</option>
          ))}
        </select>

        <input
          type="text"
          placeholder={i.filterSkillsPlaceholder}
          value={skillQuery}
          onChange={(e) => setSkillQuery(e.target.value)}
          style={S.filterInput}
          aria-label={i.filterBySkill}
        />
      </div>

      {/* P1-3: small muted legend, shown once over the results list */}
      {candidates.some((c) => (c.criteriaAssessment?.length ?? 0) > 0) && (
        <div style={{ padding: '6px 16px', borderBottom: '1px solid var(--border)' }}>{renderLegend()}</div>
      )}

      {/* P2-1: honest per-source status strip — shows WHY a search is empty */}
      {Object.keys(sourceStatuses).length > 0 && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, padding: '8px 16px', borderBottom: '1px solid var(--border)', fontSize: 11.5, color: 'var(--text-muted)', backgroundColor: 'var(--bg-elevated)' }}>
            <span style={{ fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{i.sourcesLabel}</span>
            {SOURCES.filter((s) => sourceStatuses[s] !== undefined).map((s) => {
              const st = sourceStatuses[s]!;
              const color = st === 'live' ? 'var(--success)' : st === 'unavailable' ? 'var(--danger)' : st === 'demo' ? 'var(--accent)' : st === 'searching' ? 'var(--warning)' : 'var(--text-muted)';
              const label = st === 'live' ? i.srcLive : st === 'empty' ? i.srcEmpty : st === 'unavailable' ? i.srcUnavailable : st === 'demo' ? i.srcDemo : i.srcSearching;
              return (
                <span key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
                  {SOURCE_LABELS[s]}<span style={{ color }}> · {label}</span>
                </span>
              );
            })}
          </div>
          {(() => {
            const assist = Object.keys(sourceStatuses).filter((s) => isAssistSource(s));
            const allDown = assist.length > 0 && assist.every((s) => sourceStatuses[s] === 'unavailable');
            return allDown ? (
              <div style={{ padding: '8px 16px', borderBottom: '1px solid var(--border)', fontSize: 12, color: 'var(--warning)' }}>
                ⚠ {i.assistKeyHint}
              </div>
            ) : null;
          })()}
        </>
      )}

      <div style={S.list}>
        {loading ? (
          <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[0, 1, 2, 3, 4].map((n) => (
              <div key={n} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <Skeleton width={36} height={36} borderRadius={8} />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <Skeleton width={`${55 + n * 7}%`} height={14} />
                  <Skeleton width="45%" height={12} />
                  <Skeleton width="60%" height={12} />
                </div>
              </div>
            ))}
          </div>
        ) : error !== null ? (
          <div style={{ ...S.loadingRow, color: 'var(--danger)' }}>{error}</div>
        ) : filtered.length === 0 ? (
          <div style={S.loadingRow}>
            {candidates.length === 0 ? i.noResults : i.noFilterMatch}
          </div>
        ) : (
          <>
            {filtered.map((c) => (
              <div
                key={c.id}
                ref={selected?.id === c.id ? selectedRef : null}
                role="button"
                tabIndex={0}
                onClick={() => setSelected(c)}
                onKeyDown={(e) => e.key === 'Enter' && setSelected(c)}
                style={{
                  ...S.candidateRow,
                  backgroundColor: selected?.id === c.id ? 'var(--accent-subtle)' : undefined,
                }}
              >
                {c.score < 0 ? (
                  <span style={S.naChip} title={i.scoreUnavailable}>n/a</span>
                ) : (
                  <GradeChip score={c.score} size="sm" />
                )}
                <div style={S.candidateInfo}>
                  <div style={S.candidateName}>
                    {c.name}
                    {c.acquiredVia === 'demo' || isDemoSource(c.sourceId) ? (
                      <span style={S.demoBadge} title="Synthetic demo data">{i.demo}</span>
                    ) : isAssistSource(c.sourceId) ? (
                      <span style={S.assistBadge} title="Public search result — open to review before importing">{i.assist}</span>
                    ) : null}
                  </div>
                  <div style={S.candidateTitle}>{c.title ?? c.location ?? '—'}</div>
                  {c.summary && <div style={S.candidateWhy}>{c.summary}</div>}
                  <div style={S.candidateMeta}>
                    <SourceBadge sourceId={c.sourceId} size="sm" />
                    {c.skills.slice(0, 2).map((sk) => (
                      <span key={sk} style={S.skillChip}>{sk}</span>
                    ))}
                  </div>
                  {c.criteriaAssessment && c.criteriaAssessment.length > 0 && (
                    <div style={S.criteriaStrip}>
                      {c.criteriaAssessment.map((a, idx) => (
                        <span
                          key={idx}
                          style={{ ...S.criteriaStripIcon, color: STATUS_META[a.status].color }}
                          title={`${a.criterion}: ${a.status === 'unknown' ? i.critNoData : a.evidence || statusLabel(a.status)}`}
                          aria-label={`${a.criterion} — ${statusLabel(a.status)}`}
                        >
                          {STATUS_META[a.status].icon}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {isFavorited(c.id) && (
                  <span style={{ color: 'var(--accent)', fontSize: 12 }}>★</span>
                )}
              </div>
            ))}
            {candidates.length < total && (
              <div style={{ padding: '12px 16px', textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={() => void loadMore()}
                  disabled={loadingMore}
                  style={{
                    fontSize: 13,
                    padding: '6px 16px',
                    borderRadius: 8,
                    border: '1px solid var(--border)',
                    backgroundColor: 'var(--bg-elevated)',
                    color: 'var(--text-secondary)',
                    cursor: loadingMore ? 'not-allowed' : 'pointer',
                    opacity: loadingMore ? 0.6 : 1,
                  }}
                >
                  {loadingMore ? i.loadingMore : `${i.loadMore} (${total - candidates.length})`}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  // Detail panel — shown on desktop always, on mobile only when candidate selected
  const detailPanel = (
    <div style={S.detailPanel}>
      {selected === null ? (
        <div style={S.emptyDetail}>Select a candidate to view details</div>
      ) : (
        <>
          <div style={S.detailHeader}>
            <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 4, flex: 1, minWidth: 0 }}>
              {/* Back button on mobile */}
              {isMobile && (
                <button
                  type="button"
                  style={S.backBtn}
                  onClick={() => setSelected(null)}
                >
                  {i.backToList}
                </button>
              )}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                <div style={S.detailAvatar}>{getInitials(selected.name)}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h2 style={S.detailName}>
                    {selected.name}
                    {isDemoSource(selected.sourceId) && <span style={S.demoBadge} title="Synthetic demo data">{i.demo}</span>}
                    {isAssistSource(selected.sourceId) && (
                      <span style={S.assistBadge} title="Public search result — open to review before importing">{i.assist}</span>
                    )}
                  </h2>
                  <p style={S.detailTitle}>
                    {selected.title ?? '—'} · {selected.location ?? '—'}
                  </p>
                  <SourceBadge sourceId={selected.sourceId} showLabel />
                </div>
                <div style={S.detailActions}>
                  <button
                    type="button"
                    onClick={() => {
                      void navigator.clipboard.writeText(selected.profileUrl);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 1500);
                    }}
                    style={S.iconBtn}
                    title="Copy profile URL"
                  >
                    {copied ? '✓' : <IconCopy size={14} />}
                  </button>
                  <a
                    href={selected.profileUrl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => submitSignal({ source: selected.sourceId, url: selected.profileUrl, signal: 'opened' })}
                    style={S.iconBtn}
                    title={i.viewProfile}
                  >
                    <IconExternalLink size={14} />
                  </a>
                  <button
                    type="button"
                    onClick={() => void toggleFavorite(selected)}
                    disabled={favLoading === selected.id}
                    style={isFavorited(selected.id) ? S.favBtnActive : S.iconBtn}
                    title={isFavorited(selected.id) ? i.removeFromFavorites : i.addToFavorites}
                  >
                    <IconStar size={14} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div style={S.detailBody}>
            {/* Assist provenance note — public search result, open before importing */}
            {isAssistSource(selected.sourceId) && (
              <div style={S.assistNote}>{i.assistNote}</div>
            )}
            {/* Score */}
            <div style={S.section}>
              <span style={S.sectionLabel}>{i.score}</span>
              {selected.score < 0 ? (
                <span style={S.scoreUnavailable} title={i.scoreUnavailableHint}>{i.scoreUnavailable}</span>
              ) : (
                <div style={S.scoreRow}>
                  <span style={S.scoreNum}>{selected.score}</span>
                  <div style={{ flex: 1 }}>
                    <ScoreBar score={selected.score} />
                  </div>
                  <GradeChip score={selected.score} />
                </div>
              )}
            </div>

            {/* Search filters (include/exclude) — these shape the search, not per-candidate (P1-1) */}
            {(() => {
              const f = searchCriteria ? searchFilters(searchCriteria) : {};
              if (!f.include && !f.exclude) return null;
              return (
                <div style={S.section}>
                  <span style={S.sectionLabel}>{i.searchFilters}</span>
                  {f.include && (
                    <div style={S.filterItem}>
                      <span style={S.filterLabel}>{i.filterInclude}</span>
                      <span style={S.filterValue}>{f.include}</span>
                    </div>
                  )}
                  {f.exclude && (
                    <div style={S.filterItem}>
                      <span style={S.filterLabel}>{i.filterExclude}</span>
                      <span style={S.filterValue}>{f.exclude}</span>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Summary */}
            {selected.summary && (
              <div style={S.section}>
                <span style={S.sectionLabel}>Summary</span>
                <p style={S.summary}>{selected.summary}</p>
              </div>
            )}

            {/* Per-criterion assessment (P1-3) */}
            {selected.criteriaAssessment && selected.criteriaAssessment.length > 0 && (
              <div style={S.section}>
                <span style={S.sectionLabel}>{i.criteriaTitle}</span>
                <div style={S.criteriaList}>
                  {selected.criteriaAssessment.map((a, idx) => (
                    <div
                      key={idx}
                      style={S.criteriaItem}
                      title={a.status === 'unknown' ? i.critNoData : a.evidence || statusLabel(a.status)}
                      aria-label={`${a.criterion} — ${statusLabel(a.status)}`}
                    >
                      <span aria-hidden style={{ ...S.criteriaIcon, color: STATUS_META[a.status].color }}>
                        {STATUS_META[a.status].icon}
                      </span>
                      <span style={S.criteriaCriterion}>{a.criterion}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Skills */}
            <div style={S.section}>
              <span style={S.sectionLabel}>{i.skills}</span>
              <div style={S.skillsWrap}>
                {selected.skills.map((sk) => (
                  <span key={sk} style={S.skillChip}>{sk}</span>
                ))}
              </div>
            </div>

            {/* Experience */}
            {selected.experience && (
              <div style={S.section}>
                <span style={S.sectionLabel}>{i.experience}</span>
                <span style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
                  {selected.experience}
                </span>
              </div>
            )}

            {/* Gaps */}
            {selected.gaps && selected.gaps.length > 0 && (
              <div style={S.section}>
                <span style={S.sectionLabel}>{i.gaps}</span>
                <div style={S.skillsWrap}>
                  {selected.gaps.map((g) => (
                    <span key={g} style={S.gapChip}>{g}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );

  return (
    <div
      style={{
        ...S.page,
        flexDirection: isMobile ? 'column' : 'row',
      }}
    >
      {/* Mobile: show only list OR detail based on selection */}
      {isMobile ? (
        <>
          {showListOnMobile && listPanel}
          {showDetailOnMobile && detailPanel}
        </>
      ) : (
        <>
          {listPanel}
          {detailPanel}
        </>
      )}
    </div>
  );
}
